import { useRef, useCallback, useEffect } from 'react'
import { useLabelStore } from '@/stores/labelStore'
import { useElementsStore } from '@/stores/elementsStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { CanvasElement } from './CanvasElement'
import { GridOverlay } from './GridOverlay'

const MM_TO_PX = 3.78 // Approximate conversion for screen display

export function LabelCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { label } = useLabelStore()
  const {
    elements,
    selectedId,
    selectedIds,
    selectElement,
    toggleSelectElement,
    clearSelection,
    moveElement,
    moveElements,
    deleteElement,
    deleteElements,
    resizeElement,
    updateElement,
    copySelectedElements,
    pasteElements,
    duplicateElement
  } = useElementsStore()
  const { zoom, showGrid, gridSize, setLastClickPosition } = useSettingsStore()

  // Keyboard navigation for moving elements
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Handle copy/paste/duplicate regardless of selection
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedIds.length > 0) {
          e.preventDefault()
          copySelectedElements()
        }
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()
        pasteElements()
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        if (selectedId) {
          e.preventDefault()
          duplicateElement(selectedId)
        }
        return
      }

      // Handle delete for any selection
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.length > 0) {
          e.preventDefault()
          if (selectedIds.length > 1) {
            deleteElements(selectedIds)
          } else if (selectedId) {
            deleteElement(selectedId)
          }
        }
        return
      }

      // Ignore movement if no element is selected
      if (selectedIds.length === 0) return

      // Movement step: use gridSize if grid is enabled, otherwise 1mm
      // Shift key multiplies by 5 for faster movement
      const baseStep = showGrid ? gridSize : 1
      const step = e.shiftKey ? baseStep * 5 : baseStep

      let deltaX = 0
      let deltaY = 0
      let moved = false

      switch (e.key) {
        case 'ArrowUp':
          deltaY = -step
          moved = true
          break
        case 'ArrowDown':
          deltaY = step
          moved = true
          break
        case 'ArrowLeft':
          deltaX = -step
          moved = true
          break
        case 'ArrowRight':
          deltaX = step
          moved = true
          break
      }

      if (moved) {
        e.preventDefault()
        if (selectedIds.length > 1) {
          // Move all selected elements together
          moveElements(selectedIds, deltaX, deltaY)
        } else if (selectedId) {
          // Move single element
          const element = elements.find((el) => el.id === selectedId)
          if (element) {
            const newX = Math.max(0, element.position.x + deltaX)
            const newY = Math.max(0, element.position.y + deltaY)
            moveElement(selectedId, { x: newX, y: newY })
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedId, selectedIds, elements, showGrid, gridSize, label.width, label.height, moveElement, moveElements, deleteElement, deleteElements, copySelectedElements, pasteElements, duplicateElement])

  const canvasWidth = label.width * MM_TO_PX * zoom
  const canvasHeight = label.height * MM_TO_PX * zoom

  const canvasRef = useRef<HTMLDivElement>(null)

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Store click position in mm for new element placement (works for any click on canvas)
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect()
        const clickX = (e.clientX - rect.left) / (MM_TO_PX * zoom)
        const clickY = (e.clientY - rect.top) / (MM_TO_PX * zoom)
        setLastClickPosition(clickX, clickY)
      }

      // Only deselect if clicking directly on the canvas background
      if (e.target === e.currentTarget) {
        clearSelection()
      }
    },
    [clearSelection, zoom, setLastClickPosition]
  )

  const handleElementSelect = useCallback(
    (id: string, e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+click: toggle selection
        toggleSelectElement(id)
      } else if (selectedIds.includes(id)) {
        // Clicking on already selected element: keep selection for dragging
        // Just ensure this element is selected (for single selection case)
        if (selectedIds.length === 1) {
          selectElement(id)
        }
        // For multi-selection, do nothing - keep the selection intact for dragging
      } else {
        // Normal click on unselected element: select only this element
        selectElement(id)
      }
    },
    [selectElement, toggleSelectElement, selectedIds]
  )

  const handleElementDrag = useCallback(
    (id: string, deltaX: number, deltaY: number, isDragEnd: boolean) => {
      if (!isDragEnd) return // Only update position at drag end for smoother experience

      const element = elements.find((el) => el.id === id)
      if (!element) return

      // Convert pixel delta to mm
      const deltaXMm = deltaX / (MM_TO_PX * zoom)
      const deltaYMm = deltaY / (MM_TO_PX * zoom)

      // Snap delta to grid if enabled
      const snappedDeltaX = showGrid ? Math.round(deltaXMm / gridSize) * gridSize : deltaXMm
      const snappedDeltaY = showGrid ? Math.round(deltaYMm / gridSize) * gridSize : deltaYMm

      // Check if dragged element is part of a multi-selection
      if (selectedIds.length > 1 && selectedIds.includes(id)) {
        // Move all selected elements together
        moveElements(selectedIds, snappedDeltaX, snappedDeltaY)
      } else {
        // Single element drag
        const newX = Math.max(0, element.position.x + snappedDeltaX)
        const newY = Math.max(0, element.position.y + snappedDeltaY)
        moveElement(id, { x: newX, y: newY })
      }

      // Update last click position
      const newX = element.position.x + snappedDeltaX
      const newY = element.position.y + snappedDeltaY
      setLastClickPosition(newX, newY)
    },
    [elements, selectedIds, moveElement, moveElements, zoom, showGrid, gridSize, setLastClickPosition]
  )

  const handleElementResize = useCallback(
    (id: string, width: number | undefined, height: number | undefined, diameter: number | undefined) => {
      resizeElement(id, { width, height, diameter })
    },
    [resizeElement]
  )

  const handleTextEdit = useCallback(
    (id: string, newContent: string) => {
      updateElement(id, { content: newContent })
    },
    [updateElement]
  )

  return (
    <div
      ref={containerRef}
      className="flex-1 bg-muted/50 overflow-auto p-8"
    >
      <div
        className="min-h-full min-w-full flex items-center justify-center"
        style={{
          // Add extra space to allow scrolling to all parts of the canvas
          minWidth: canvasWidth + 64, // 64 = 2 * p-8 (32px each side)
          minHeight: canvasHeight + 64,
        }}
      >
        <div
          ref={canvasRef}
          className="relative bg-white shadow-lg border border-gray-300 shrink-0"
          style={{
            width: canvasWidth,
            height: canvasHeight,
          }}
          onMouseDown={handleCanvasMouseDown}
        >
        {showGrid && (
          <GridOverlay
            width={label.width}
            height={label.height}
            gridSize={gridSize}
            zoom={zoom}
            mmToPx={MM_TO_PX}
          />
        )}

        {elements.map((element) => (
          <CanvasElement
            key={element.id}
            element={element}
            isSelected={selectedIds.includes(element.id)}
            zoom={zoom}
            mmToPx={MM_TO_PX}
            onSelect={(e) => handleElementSelect(element.id, e)}
            onDrag={(dx, dy, isDragEnd) => handleElementDrag(element.id, dx, dy, isDragEnd)}
            onResize={(w, h, d) => handleElementResize(element.id, w, h, d)}
            onTextEdit={(newContent) => handleTextEdit(element.id, newContent)}
          />
        ))}
        </div>
      </div>
    </div>
  )
}
